import { useUser } from "@saleor/auth";
import {
  DateSelectionType,
  FIELD_TYPE
} from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import {
  useFilterCustomerBusinessUnitQuery,
  useFilterCustomerCompanyByBuQuery,
  useFilterRequireAttentionMaterialLazyQuery
} from "@saleor/graphql";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { useFormik } from "formik";
import moment from "moment-timezone";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

const useOrderFilterInquiry = ({ handleChangeFilter }) => {
  const { company, listCompany } = useUser();

  const soldToInit = listCompany?.find(e => e.soldToCode === company)
    ? `${listCompany?.find(e => e.soldToCode === company)?.soldToCode} - ${
        listCompany?.find(e => e.soldToCode === company)?.soldToName
      }`
    : "";

  const { data: listBU } = useFilterCustomerBusinessUnitQuery();

  const defaultBu = useMemo(
    () =>
      listBU?.filterCustomerBusinessUnit?.find(
        item => item?.isDefaultForInquirySearch
      ),
    [listBU]
  );

  const { data: listCompanyWithBU } = useFilterCustomerCompanyByBuQuery({
    variables: {
      first: 100,
      filter: { businessUnit: defaultBu?.value }
    },
    skip: !defaultBu?.value
  });

  const formatedCompaniesExportOrderByBusinessUnit = useMemo(() => {
    if (listCompanyWithBU) {
      const companies =
        mapEdgesToItems(listCompanyWithBU?.filterCustomerCompanyByBu)?.map(
          e => ({
            label: `${e.code} - ${e.shortName}`,
            value: e.code
          })
        ) || [];
      return [{ label: "All", value: "ALL" }, ...companies];
    }
    return [];
  }, [listCompanyWithBU]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      soldTo: soldToInit,
      materialCodeName: "",
      soNo: "",
      contractNo: "",
      dpNo: "",
      purchaseOrderNo: "",
      invoiceNo: "",
      bu: defaultBu?.value || "",
      company: formatedCompaniesExportOrderByBusinessUnit[0]?.value || "",
      dateSelectionType: DateSelectionType.CREATE_DATE,
      gte: null,
      lte: null,
      createDate: null,
      requestDeliveryDate: null,
      lastUpdate: null
    },
    onSubmit: data => {
      const newData = { ...data };
      delete newData.gte;
      delete newData.lte;

      const submitData = { ...newData };
      delete submitData.dateSelectionType;

      for (const [key, value] of Object.entries(submitData)) {
        if (!value) {
          delete submitData[key];
        }
      }
      const dateRange = {
        gte: data.gte ? moment(data.gte).format("YYYY-MM-DD") : undefined,
        lte: data.lte ? moment(data.lte).format("YYYY-MM-DD") : undefined
      };
      if (data.dateSelectionType === DateSelectionType.CREATE_DATE) {
        delete submitData.requestDeliveryDate;
        submitData.createDate = dateRange;
      } else {
        delete submitData.createDate;
        submitData.requestDeliveryDate = dateRange;
        submitData.lastUpdate = dateRange;
      }

      handleChangeFilter(submitData);
    }
  });

  const intl = useIntl();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const [
    getMaterialCodeDataSuggestion,
    {
      data: fetchMaterialCodeDataSuggestion,
      loading: loadingMaterialCodeDataSuggestion
    }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    formik.resetForm();
    formik.setFieldValue("bu", defaultBu.value);
    setTimeout(() => setIsClean(false), 500);
  }, [listBU]);

  const formData = useMemo(
    () => [
      {
        title: intl.formatMessage({ defaultMessage: "Sold to" }),
        name: "soldTo",
        placeholder: intl.formatMessage({ defaultMessage: "ค้นหา Sold to..." }),
        type: FIELD_TYPE.SINGLE_INPUT,
        disable: true
      },
      {
        title: intl.formatMessage({ defaultMessage: "PO No." }),
        name: "purchaseOrderNo",
        placeholder: intl.formatMessage({ defaultMessage: "ระบุ PO No." }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({ defaultMessage: "SO No." }),
        name: "soNo",
        placeholder: intl.formatMessage({ defaultMessage: "ระบุ SO No." }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "Contract No." }),
        name: "contractNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ Contract No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({ defaultMessage: "DP No." }),
        name: "dpNo",
        placeholder: intl.formatMessage({ defaultMessage: "ระบุ D/P No." }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Invoice No. (Billing No.)"
        }),
        name: "invoiceNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ Invoice No. (Billing No.)"
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({ defaultMessage: "BU" }),
        name: "bu",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก BU" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: listBU?.filterCustomerBusinessUnit || [],
        required: true
      },
      {
        title: intl.formatMessage({ defaultMessage: "Company" }),
        name: "company",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก Company" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: formatedCompaniesExportOrderByBusinessUnit || [],
        required: true
      },
      {
        title: intl.formatMessage({ defaultMessage: "Date Range" }),
        name: "dateSelectionType",
        options: [
          {
            label: intl.formatMessage({ defaultMessage: "Create Date" }),
            value: DateSelectionType.CREATE_DATE
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Request delivery date"
            }),
            value: DateSelectionType.LAST_UPDATE
          }
        ],
        type: FIELD_TYPE.RADIO_GROUP
      },
      {
        title: intl.formatMessage({ defaultMessage: "Start Date" }),
        name: "gte",
        type: FIELD_TYPE.DATEPICKER
      },
      {
        title: intl.formatMessage({ defaultMessage: "End Date" }),
        name: "lte",
        type: FIELD_TYPE.DATEPICKER
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Material Code / Description"
        }),
        name: "materialCodeName",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Material Code / Description"
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT
      }
    ],
    [listBU, listCompanyWithBU, company]
  );

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      fetchMaterialCodeDataSuggestion,
      getMaterialCodeDataSuggestion,
      loadingMaterialCodeDataSuggestion,
      listCompanyWithBU,
      listBU,
      handleCleanForm,
      formData,
      formatedCompaniesExportOrderByBusinessUnit
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      formatedCompaniesExportOrderByBusinessUnit,
      fetchMaterialCodeDataSuggestion,
      getMaterialCodeDataSuggestion,
      loadingMaterialCodeDataSuggestion,
      listCompanyWithBU,
      listBU,
      handleCleanForm,
      formData
    ]
  );
};

export default useOrderFilterInquiry;
